import { fromJS } from 'immutable';
import { loading, success, error } from 'reducers';

import * as ActionType from './actions';
import { DefaultFilters } from './constants';
import { normalizeSearchResponse, suggestionToSubject } from './investorUtils';

// Investor Outreach subscription state (ATAI-260 / S7), cached here so the search route and the
// Account card read it once. `status`: ACTIVE | UNSUBSCRIBED (pending cancellation) | null.
const ENTITLEMENT_DEFAULT = {
  loaded: false,
  loading: false,
  entitled: false,
  status: null,
  endDate: null,
  connectCancelledWithIo: false,
  endedDate: null,
  error: null,
};

// `subject`, `suggestions` and `recentSubjects` are held as plain JS (set, not
// merge) because they are handed straight to SuggestedInput and to the recents
// helpers; `filters` is Immutable so the UI reads it like the other slices.
const defaultState = fromJS({
  error: null,
  loading: false,
  subjectLabel: '',
  suggestionsLoading: false,
  suggestionsQuery: null,
  subjectPreselected: false,
  recentLoaded: false,
  filters: DefaultFilters,
  total: 0,
  capped: false,
  searched: false,
  stale: false,
  entitlement: ENTITLEMENT_DEFAULT,
})
  .set('subject', null)
  .set('suggestions', [])
  .set('recentSubjects', [])
  .set('investors', [])
  .set('properties', [])
  .set('warnings', [])
  .set('subjectLocation', null);

export default function reducer(state = defaultState, action) {
  const { response } = action;

  switch (action.type) {
    case ActionType.SEARCH_SUBJECT_SUGGESTIONS:
      return state.set('suggestionsLoading', true);

    // A lone suggestion is preselected so the user can search without a second
    // click. It is a silent selection: the typed label is left alone (the user
    // may still be mid-word) and the panel keeps its suggestions, so the only
    // visible effects are the highlighted row and the enabled CTA.
    case ActionType.SEARCH_SUBJECT_SUGGESTIONS_SUCCESS: {
      // `property/suggestionsnew` answers with every address type it knows —
      // P property, S street, C city, N county, T state, Z zip — and this call
      // passes no `types`, so it gets all of them. Investor Outreach searches
      // out from one address, so only the rows that carry a `streetAddress`
      // can serve as a subject; the city/county/state/zip rows are dropped.
      //
      // Filtered here rather than in SubjectInput so the lone-suggestion
      // preselect below counts the same rows the panel shows: a response of one
      // property plus one city would otherwise leave a single visible row that
      // the preselect had passed over.
      const suggestions = (response || []).filter((item) => item && item.streetAddress);
      // An explicitly picked subject outranks a preselect and is never
      // overwritten by a result set landing late; a stale preselect is dropped.
      const picked = state.get('subjectPreselected') ? null : state.get('subject');
      const single = suggestions.length === 1 ? suggestionToSubject(suggestions[0]) : null;
      const preselect = !picked && single && single.propertyId ? single : null;

      return state
        .set('suggestions', suggestions)
        .set('suggestionsQuery', action.query)
        .set('suggestionsLoading', false)
        .set('recentLoaded', false)
        .set('subject', preselect || picked)
        .set('subjectPreselected', Boolean(preselect));
    }

    case ActionType.SEARCH_SUBJECT_SUGGESTIONS_ERROR:
      return state
        .set('suggestions', [])
        .set('suggestionsQuery', action.query)
        .set('suggestionsLoading', false);

    // Typing invalidates the subject, picked or preselected — the label no
    // longer matches the property it resolved to, so the CTA goes back to
    // unresolved until a row is picked or the next result set preselects one.
    // Keeping a picked subject here let Find/Enter on edited (even invalid)
    // text re-run the search for the previous address (ATAI-491).
    case ActionType.SET_SUBJECT_LABEL:
      return state
        .set('subjectLabel', action.label || '')
        .set('subject', null)
        .set('subjectPreselected', false)
        .set('stale', false);

    case ActionType.SET_SUBJECT:
      return state
        .set('subject', action.subject || null)
        .set('subjectLabel', (action.subject && action.subject.label) || '')
        .set('subjectPreselected', false)
        .set('suggestions', [])
        .set('suggestionsQuery', null)
        .set('stale', Boolean(action.subject && action.subject.propertyId));

    case ActionType.CLEAR_SUBJECT:
      return state
        .set('subject', null)
        .set('subjectLabel', '')
        .set('subjectPreselected', false)
        .set('suggestions', [])
        .set('suggestionsQuery', null)
        .set('stale', false);

    case ActionType.SET_RECENT_SUBJECTS:
      return state
        .set('recentSubjects', action.items || [])
        .set('recentLoaded', Boolean(action.loaded));

    // Live filtering: re-search on a filter change once searched, never dropping a pending search.
    case ActionType.SET_INVESTOR_FILTER:
      return state
        .setIn(['filters', action.name], fromJS(action.value))
        .set('stale', state.get('stale') || Boolean(state.get('searched') && state.get('subject')));

    case ActionType.RESET_INVESTOR_FILTERS:
      return state
        .set('filters', fromJS(DefaultFilters))
        .set('stale', state.get('stale') || Boolean(state.get('searched') && state.get('subject')));

    // A Find on an unresolved address drops the previous search's results so
    // they aren't left on screen under an address they don't belong to; the
    // page falls back to its empty state (ATAI-491).
    case ActionType.CLEAR_INVESTOR_RESULTS:
      return state
        .set('investors', [])
        .set('properties', [])
        .set('warnings', [])
        .set('subjectLocation', null)
        .set('total', 0)
        .set('capped', false)
        .set('searched', false)
        .set('error', null);

    case ActionType.SEARCH_INVESTORS:
      return loading(state).set('stale', false);

    case ActionType.SEARCH_INVESTORS_ERROR:
      return error(state, action).set('searched', true);

    case ActionType.SEARCH_INVESTORS_SUCCESS: {
      const result = normalizeSearchResponse(response);

      return success(
        state
          .set('investors', result.investors)
          .set('properties', result.properties)
          .set('subjectLocation', result.subjectLocation)
          .set('warnings', result.warnings)
          .set('total', result.totalCount)
          .set('capped', result.capped)
          .set('searched', true)
      );
    }

    // --- Investor Outreach entitlement (ATAI-260 / S7) ---
    case ActionType.FETCH_IO_ENTITLEMENT:
      return state.setIn(['entitlement', 'loading'], true).setIn(['entitlement', 'error'], null);

    case ActionType.FETCH_IO_ENTITLEMENT_SUCCESS:
      return state.set(
        'entitlement',
        fromJS({
          loaded: true,
          loading: false,
          error: null,
          entitled: Boolean(action.entitled),
          status: action.status || null,
          endDate: action.endDate || null,
          connectCancelledWithIo: Boolean(action.connectCancelledWithIo),
          endedDate: action.endedDate || null,
        })
      );

    case ActionType.FETCH_IO_ENTITLEMENT_ERROR:
      return state
        .setIn(['entitlement', 'loading'], false)
        .setIn(['entitlement', 'loaded'], true)
        .setIn(['entitlement', 'entitled'], false)
        .setIn(['entitlement', 'error'], action.error || true);

    case ActionType.RESET_IO_ENTITLEMENT:
      return state.set('entitlement', fromJS(ENTITLEMENT_DEFAULT));

    default:
      return state;
  }
}
